import { PlusOutlined } from "@ant-design/icons";
import { Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { FieldArray, useField, useFormikContext } from "formik";
import { get, isNil, sumBy } from "lodash";
import React, { useState } from "react";

import DeleteRecordDialog from "components/form/standard/DeleteRecordDialog";
import { formatDateForDisplay } from "components/form/standard/utils/formatting";
import { TextFieldCurrency } from "components/form/TextFieldCurrency";
import { TextFieldString } from "components/form/TextFieldString";
import { DealBaseMetadata } from "constants/objectMetadata/dealMetadataV2";
import { formatCurrency } from "pages/deal/utils/reporting";
import { LookupItem } from "types/api/deal/form";
import { InvoiceStatusEnum } from "types/api/deal/invoice";

export const INVOICE_STATUS_ITEMS_MAP = {
  [InvoiceStatusEnum.new]: "New",
  [InvoiceStatusEnum.sent]: "Sent",
  [InvoiceStatusEnum.partially_paid]: "Paid (Partial)",
  [InvoiceStatusEnum.paid]: "Paid",
};

export const INVOICE_STATUS_ITEMS: LookupItem[] = [
  { key: InvoiceStatusEnum.new, label: "New" },
  { key: InvoiceStatusEnum.sent, label: "Sent" },
  { key: InvoiceStatusEnum.partially_paid, label: "Paid (Partial)" },
  { key: InvoiceStatusEnum.paid, label: "Paid" },
];

/**
 * Cell for editing/displaying Invoice Date.
 * On non-editing state, shows a formatted date string.
 * On editing state, shows a DatePicker.
 */
function InvoiceDateCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (rowIndex: number, fieldName: string) => boolean;
  startEditing: (rowIndex: number, fieldName: string) => void;
  stopEditing: () => void;
}) {
  const { setFieldValue } = useFormikContext();
  const [dateField] = useField(`invoices[${index}].date`);

  return (
    <TableCell
      onClick={() => startEditing(index, "date")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "date") ? (
        <TextField
          fullWidth
          type="date"
          autoFocus
          value={dateField.value || ""}
          onChange={(e) =>
            setFieldValue(`invoices[${index}].date`, e.target.value)
          }
          onBlur={() => stopEditing()}
        />
      ) : (
        formatDateForDisplay(dateField.value)
      )}
    </TableCell>
  );
}

/**
 * Cell for editing/displaying the currency Amount.
 * On non-editing state, shows a formatted currency string.
 * On editing state, shows a TextFieldCurrency.
 */
function InvoiceAmountCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (rowIndex: number, fieldName: string) => boolean;
  startEditing: (rowIndex: number, fieldName: string) => void;
  stopEditing: () => void;
}) {
  const [amountField] = useField(`invoices.${index}.amount`);

  return (
    <TableCell
      onClick={() => startEditing(index, "amount")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "amount") ? (
        <TextFieldCurrency
          fieldName={`invoices.${index}.amount`}
          showLabel={false}
          autoFocus
          onBlur={stopEditing}
        />
      ) : (
        formatCurrency(amountField.value)
      )}
    </TableCell>
  );
}

/**
 * Cell for editing/displaying the Description.
 * On non-editing state, shows a plain text.
 * On editing state, shows a TextFieldString.
 */
function InvoiceDescriptionCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (rowIndex: number, fieldName: string) => boolean;
  startEditing: (rowIndex: number, fieldName: string) => void;
  stopEditing: () => void;
}) {
  const [descriptionField] = useField(`invoices[${index}].description`);

  return (
    <TableCell
      onClick={() => startEditing(index, "description")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "description") ? (
        <TextFieldString
          fieldName={`invoices[${index}].description`}
          showLabel={false}
          autoFocus
          onBlur={stopEditing}
        />
      ) : (
        descriptionField.value || "--"
      )}
    </TableCell>
  );
}

/**
 * Cell for editing/displaying the Invoice Number.
 * On non-editing state, shows a plain text.
 * On editing state, shows a TextFieldString.
 */
function InvoiceNumberCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (rowIndex: number, fieldName: string) => boolean;
  startEditing: (rowIndex: number, fieldName: string) => void;
  stopEditing: () => void;
}) {
  const [invoiceNumberField] = useField(`invoices[${index}].invoice_code`);

  return (
    <TableCell
      onClick={() => startEditing(index, "invoice_code")}
      sx={(theme) => ({
        "&:hover": {
          cursor: "pointer",
          bgcolor: theme.palette.primary.lighter,
        },
      })}
    >
      {isEditing(index, "invoice_code") ? (
        <TextFieldString
          fieldName={`invoices[${index}].invoice_code`}
          showLabel={false}
          autoFocus
          onBlur={stopEditing}
        />
      ) : (
        invoiceNumberField.value || "--"
      )}
    </TableCell>
  );
}

/**
 * Cell for editing/displaying the Status.
 * On non-editing state, shows a plain text.
 * On editing state, shows a SelectOption.
 */
function InvoiceStatusCell({
  index,
  isEditing,
  startEditing,
  stopEditing,
}: {
  index: number;
  isEditing: (rowIndex: number, fieldName: string) => boolean;
  startEditing: (rowIndex: number, fieldName: string) => void;
  stopEditing: () => void;
}) {
  const [statusField] = useField(`invoices[${index}].status`);

  return (
    <TableCell>
      {get(INVOICE_STATUS_ITEMS_MAP, statusField.value, "--")}
    </TableCell>
  );
}

/**
 * This component renders a table of invoices with editable cells.
 * Each cell can be clicked to enter an edit mode, where the user
 * can modify the value. A remove button is included to remove rows.
 * A "Add Invoice" button is provided to append new invoices.
 */
export default function InvoicesField() {
  const [invoicesField] = useField("invoices");

  // Track which cell is currently being edited
  const [editingCell, setEditingCell] = useState<{
    row: number | null;
    field: string | null;
  }>({ row: null, field: null });
  const [rowIndexToRemove, setRowIndexToRemove] = useState<number | undefined>(
    undefined
  );

  /**
   * Check if a given (row, field) pair is currently in editing mode.
   */
  const isEditing = (rowIndex: number, fieldName: string) =>
    editingCell.row === rowIndex && editingCell.field === fieldName;

  /**
   * Start editing a specific cell (row, field).
   */
  const startEditing = (rowIndex: number, fieldName: string) => {
    setEditingCell({ row: rowIndex, field: fieldName });
  };

  /**
   * Stop editing and revert to display mode.
   */
  const stopEditing = () => {
    setEditingCell({ row: null, field: null });
  };

  return (
    <Stack spacing={1}>
      <InputLabel>{DealBaseMetadata.invoices.displayName}</InputLabel>
      <FieldArray
        name="invoices"
        render={({ remove, push }) => (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 800, tableLayout: "fixed" }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>#</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Invoice Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center" width={60}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {invoicesField.value?.map((invoice: any, index: number) => (
                    <TableRow
                      key={`invoices-table-row-${index}`}
                      sx={{
                        // Override default row hover so each cell can have its own hover
                        "&:hover": {
                          bgcolor: "transparent !important",
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <InvoiceDateCell
                        index={index}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <InvoiceAmountCell
                        index={index}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <InvoiceDescriptionCell
                        index={index}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <InvoiceNumberCell
                        index={index}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <InvoiceStatusCell
                        index={index}
                        isEditing={isEditing}
                        startEditing={startEditing}
                        stopEditing={stopEditing}
                      />
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => setRowIndexToRemove(index)}
                        >
                          <Clear />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Footer row for total amounts (if needed) */}
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} />
                    <TableCell colSpan={1} align="right">
                      <Typography fontWeight="bold">Total:</Typography>
                    </TableCell>
                    <TableCell colSpan={2} align="right">
                      <Typography fontWeight="bold">
                        {formatCurrency(sumBy(invoicesField.value, "amount"))}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end">
              <Box sx={{ pt: 2.5, pr: 2.5, pb: 2.5 }}>
                <Button
                  color="primary"
                  startIcon={<PlusOutlined />}
                  onClick={() =>
                    push({
                      date: null,
                      amount: 0,
                      description: "",
                      invoice_code: "",
                    })
                  }
                  variant="dashed"
                  sx={{ bgcolor: "transparent !important" }}
                >
                  Add Invoice
                </Button>
              </Box>
            </Stack>
            <DeleteRecordDialog
              open={!isNil(rowIndexToRemove)}
              onClose={() => setRowIndexToRemove(undefined)}
              onConfirm={() => {
                !isNil(rowIndexToRemove) && remove(rowIndexToRemove);
                setRowIndexToRemove(undefined);
              }}
              displayName={"invoice"}
            />
          </>
        )}
      />
    </Stack>
  );
}
